import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiArrowRight, FiChevronDown, FiChevronUp, FiHelpCircle, FiInfo } from 'react-icons/fi';

const GUIDE_STEPS = [
    {
        key: 'contacts',
        to: '/dashboard/correos/contactos',
        label: 'Contactos',
        requirement: 'Necesario',
        tone: 'required',
        description: 'Es tu base de destinatarios. Cada persona necesita como mínimo un email válido y activo para poder entrar en una campaña.',
        suggestion: 'Si son pocos, cargalos a mano. Si tenés muchos, usa Importar. Nombre, empresa, puesto y tags no son obligatorios, pero hacen mucho más útiles los filtros y la personalización.',
    },
    {
        key: 'lists',
        to: '/dashboard/correos/listas',
        label: 'Listas',
        requirement: 'Recomendado',
        tone: 'recommended',
        description: 'Agrupa contactos por objetivo: RRHH Argentina, Fintech, Software Factory, Recruiters, etc.',
        suggestion: 'No es obligatorio crear listas. Si una campaña no tiene listas, se pueden usar todos los contactos activos. Conviene usarlas para no mezclar públicos.',
    },
    {
        key: 'import',
        to: '/dashboard/correos/importar',
        label: 'Importar',
        requirement: 'Opcional',
        tone: 'optional',
        description: 'Carga contactos en lote desde XLSX, CSV, TXT, JSON o DOCX y evita tener que agregarlos uno por uno.',
        suggestion: 'El dato realmente importante es el email. Podés indicar una fuente para recordar de donde salieron y, si querés, asignarlos directamente a una lista.',
    },
    {
        key: 'templates',
        to: '/dashboard/correos/plantillas',
        label: 'Plantillas',
        requirement: 'Opcional',
        tone: 'optional',
        description: 'Guarda asuntos y mensajes que quieras reutilizar en varias campañas.',
        suggestion: 'Para tu caso conviene tener al menos una plantilla de envío de CV y otra de seguimiento. Las variables como {{nombre}} se reemplazan por cada destinatario.',
    },
    {
        key: 'campaigns',
        to: '/dashboard/correos/campanas',
        label: 'Campañas',
        requirement: 'Necesario',
        tone: 'required',
        description: 'Es el envío en sí: define asunto, mensaje, remitente, destinatarios, adjuntos, velocidad y programación.',
        suggestion: 'Orden seguro: crear -> guardar -> editar -> enviar una prueba -> revisar -> encolar. No encoles una campaña grande sin hacer primero una prueba.',
    },
    {
        key: 'queue',
        to: '/dashboard/correos/cola',
        label: 'Cola e historial',
        requirement: 'Automático',
        tone: 'automatic',
        description: 'Muestra qué pasó con cada destinatario: pendiente, procesando, enviado, fallido, omitido o bloqueado.',
        suggestion: 'No tenés que cargar nada acá. Usalo para controlar el envío y leer el motivo de un fallo antes de reintentar.',
    },
    {
        key: 'blocks',
        to: '/dashboard/correos/bloqueos',
        label: 'Bloqueos',
        requirement: 'Recomendado',
        tone: 'recommended',
        description: 'Es la lista de direcciones que nunca deben volver a entrar en una cola de envío.',
        suggestion: 'Usalo para bajas, rebotes permanentes o personas que no querés volver a contactar. Es una capa de seguridad adicional.',
    },
];

const readInitialOpen = () => {
    try {
        const saved = window.localStorage.getItem('pbmail-guide-open');
        return saved === null ? true : saved === '1';
    } catch (_) {
        return true;
    }
};

export const EmailModuleGuide = () => {
    const { pathname } = useLocation();
    const [isOpen, setIsOpen] = useState(readInitialOpen);

    const activeIndex = useMemo(() => GUIDE_STEPS.findIndex((step) => pathname === step.to || pathname.startsWith(`${step.to}/`)), [pathname]);
    const current = activeIndex >= 0 ? GUIDE_STEPS[activeIndex] : null;
    const next = activeIndex >= 0 && activeIndex < GUIDE_STEPS.length - 1 ? GUIDE_STEPS[activeIndex + 1] : null;
    const nextTo = next?.to || (current ? '/dashboard/correos' : '/dashboard/correos/contactos');
    const nextLabel = next?.label || (current ? 'Resumen' : 'Contactos');

    const toggle = () => {
        setIsOpen((previous) => {
            const value = !previous;
            try { window.localStorage.setItem('pbmail-guide-open', value ? '1' : '0'); } catch (_) {}
            return value;
        });
    };

    return (
        <section className={`PBMAIL__guide${isOpen ? ' is-open' : ''}`} aria-label="Guía paso a paso del módulo de correos">
            <button type="button" className="PBMAIL__guide_toggle" onClick={toggle} aria-expanded={isOpen}>
                <span className="PBMAIL__guide_icon"><FiHelpCircle /></span>
                <span className="PBMAIL__guide_heading">
                    <small>GUÍA PASO A PASO</small>
                    <strong>Cómo usar Correos sin perderte</strong>
                    <span>Seguí el flujo recomendado y mirá qué es necesario, opcional o automático.</span>
                </span>
                <span className="PBMAIL__guide_toggle_label">{isOpen ? 'Ocultar guía' : 'Ver guía'} {isOpen ? <FiChevronUp /> : <FiChevronDown />}</span>
            </button>

            {isOpen && (
                <div className="PBMAIL__guide_body">
                    <div className="PBMAIL__guide_steps" role="list">
                        {GUIDE_STEPS.map((step, index) => {
                            const isCurrent = index === activeIndex;
                            const isPast = activeIndex > index;
                            return (
                                <NavLink
                                    role="listitem"
                                    key={step.key}
                                    to={step.to}
                                    className={`PBMAIL__guide_step${isCurrent ? ' is-current' : ''}${isPast ? ' is-past' : ''}`}
                                >
                                    <span className="PBMAIL__guide_number">{index + 1}</span>
                                    <span className="PBMAIL__guide_step_text"><strong>{step.label}</strong><small>{step.requirement}</small></span>
                                </NavLink>
                            );
                        })}
                    </div>

                    <div className="PBMAIL__guide_context">
                        <div className="PBMAIL__guide_context_main">
                            <div className="PBMAIL__guide_context_top">
                                <span className={`PBMAIL__guide_badge is-${current?.tone || 'intro'}`}>{current ? current.requirement : 'Empezá por acá'}</span>
                                <strong>{current ? current.label : 'Flujo recomendado'}</strong>
                            </div>
                            <p>{current ? current.description : 'No necesitás usar todo. El mínimo para enviar es tener contactos y una campaña. Listas, importación y plantillas hacen el trabajo más ordenado y reutilizable.'}</p>
                            <div className="PBMAIL__guide_tip"><FiInfo /><span><b>Sugerencia:</b> {current ? current.suggestion : 'Si es tu primera vez, entrá en Contactos, cargá uno de prueba y avanzá de izquierda a derecha.'}</span></div>
                        </div>
                        <NavLink className="PBMAIL__guide_next" to={nextTo}>
                            <span>{next ? 'Siguiente paso' : current ? 'Volver al resumen' : 'Comenzar'}</span>
                            <strong>{nextLabel} <FiArrowRight /></strong>
                        </NavLink>
                    </div>
                </div>
            )}
        </section>
    );
};
